import { Copy, GitMerge, RefreshCw, ScrollText, Zap } from 'lucide-react'
import { lazy, type ReactNode, Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { FindDuplicatesModal } from '@/components/entity-resolution/FindDuplicatesModal'
import { GroupReviewPanel } from '@/components/entity-resolution/GroupReviewPanel'
import { PhotoMatchingPanel } from '@/components/faces/PhotoMatchingPanel'
import { PageShell } from '@/components/layout/PageShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useRecalculateClusters } from '@/features/entity-resolution/hooks/useClusterReview'
import { useMergeExactDuplicates } from '@/features/entity-resolution/hooks/useGroupReview'
import { ResolveWithAssistantButton } from '@/features/entity-resolution/ResolveWithAssistantButton'
import {
  FACE_RESOLUTION_TABS,
  RESOLUTION_TABS,
  type ResolutionTab,
  useResolutionTab,
} from '@/features/entity-resolution/resolution-tab'
import { usePendingFaceMatchCount } from '@/features/face-matching/hooks/useFaceMatching'
import { useTattooLeadCount } from '@/features/tattoo-leads/hooks/useTattooLeadCount'
import { useVoiceLeadCount } from '@/features/voice-leads/hooks/useVoiceLeadCount'
import {
  ADVANCED_FACE_DETECTION_EXTENSION_ID,
  HUMINT_EXTENSION_ID,
  useExtensionEnabled,
} from '@/hooks/use-extensions'
import { cn } from '@/utils/cn'
import { useCaseContext } from '../../CaseContext'

// Its own chunk: the tab names marks and persons with pieces of the HUMINT workspace, which the
// desktop app does not load (the HUMINT route is lazy for the same reason).
const TattooLeadsTab = lazy(() => import('@/features/tattoo-leads/TattooLeadsTab'))
// Its own chunk for the same reason: it names voiceprints and persons with workspace pieces.
const VoiceLeadsTab = lazy(() => import('@/features/voice-leads/VoiceLeadsTab'))

const COPY = 'cases.investigate.entityResolution'
/** A tab whose panel scrolls and pads itself (the page shell's body is flush). */
const PANEL_TAB = 'min-h-0 overflow-y-auto px-4 pb-4 md:px-6 md:pb-6'

export default function EntityResolutionPage() {
  const { t } = useTranslation()
  const { caseId } = useCaseContext()
  const faceDetection = useExtensionEnabled(ADVANCED_FACE_DETECTION_EXTENSION_ID)
  const [findOpen, setFindOpen] = useState(false)
  const duplicates = <DuplicateReview caseId={caseId} onFind={() => setFindOpen(true)} />

  return (
    <PageShell
      layout="full"
      icon={GitMerge}
      title={t('cases.investigate.entityResolution.title')}
      summary={t('cases.investigate.entityResolution.summary')}
      guide={{
        to: '/docs/entity-resolution',
        label: t('cases.investigate.entityResolution.guide.label'),
        hint: t('cases.investigate.entityResolution.guide.hint'),
      }}
    >
      {/* Photo matching is Advanced Face Detection's (ADR-0051): with it off the
          page is the duplicate review alone, with no one-tab bar above it. */}
      {faceDetection.enabled ? (
        <ResolutionTabs caseId={caseId} duplicates={duplicates} />
      ) : (
        duplicates
      )}
      <FindDuplicatesModal caseId={caseId} open={findOpen} onOpenChange={setFindOpen} />
    </PageShell>
  )
}

/** The duplicate review and its toolbar: the whole page while AFD is off, its first tab otherwise. */
function DuplicateReview({ caseId, onFind }: { caseId: string; onFind: () => void }) {
  const { t } = useTranslation()
  const location = useLocation()
  const recalculate = useRecalculateClusters(caseId)
  const mergeExact = useMergeExactDuplicates(caseId)
  return (
    <GroupReviewPanel
      caseId={caseId}
      // Rendered on the "Resolution queue" header row (overview-workspace
      // pattern) — secondary actions icon-only so they don't crowd the row.
      actions={
        <>
          <ResolveWithAssistantButton caseId={caseId} />
          <Button
            asChild
            variant="outline"
            size="icon-sm"
            aria-label={t('cases.investigate.entityResolution.toolbar.auditLog')}
            title={t('cases.investigate.entityResolution.toolbar.auditLog')}
          >
            <Link to={`${location.pathname.replace(/\/+$/, '')}/audit`}>
              <ScrollText className="size-4" />
            </Link>
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => recalculate.mutate()}
            disabled={recalculate.isPending}
            aria-label={t('cases.investigate.entityResolution.toolbar.recalculate')}
            title={t('cases.investigate.entityResolution.toolbar.recalculateTitle')}
          >
            <RefreshCw className={cn('size-4', recalculate.isPending && 'animate-spin')} />
          </Button>
          <Button variant="outline" size="sm" onClick={onFind}>
            <Copy className="size-4" />
            {t('cases.investigate.entityResolution.toolbar.findDuplicates')}
          </Button>
          <Button
            size="sm"
            onClick={() => mergeExact.mutate()}
            disabled={mergeExact.isPending}
            title={t('cases.investigate.entityResolution.toolbar.mergeExactTitle')}
          >
            <Zap className={cn('size-4', mergeExact.isPending && 'animate-spin')} />
            {t('cases.investigate.entityResolution.toolbar.mergeExact')}
          </Button>
        </>
      }
    />
  )
}

interface CountedTabProps {
  value: ResolutionTab
  label: string
  /** How many wait behind the tab, and that number in words for the tab's spoken name. */
  waiting: { count: number; spoken: string }
}

/** A tab badged with what waits behind it; the badge is spoken through the tab's name. */
function CountedTab({ value, label, waiting }: CountedTabProps) {
  return (
    <TabsTrigger
      value={value}
      aria-label={waiting.count > 0 ? `${label}, ${waiting.spoken}` : label}
    >
      {label}
      {waiting.count > 0 && (
        <Badge variant="warning" className="h-5 min-w-5 px-1.5 tabular-nums" aria-hidden="true">
          {waiting.count > 99 ? '99+' : waiting.count}
        </Badge>
      )}
    </TabsTrigger>
  )
}

/**
 * Duplicates, Photo matching and, while HUMINT is on, Tattoo matching and Voice similarity side
 * by side, all but the first badged with what waits behind them. The open tab is kept in the
 * address (`?tab=`). The two HUMINT tabs only list their leads: a row opens the comparison in
 * the workspace, and nothing is decided here.
 */
function ResolutionTabs({ caseId, duplicates }: { caseId: string; duplicates: ReactNode }) {
  const { t } = useTranslation()
  const pendingFaceCount = usePendingFaceMatchCount(caseId)
  // Tattoo matching and Voice similarity are HUMINT's: with it off there is no third or fourth
  // tab and their counts are never read.
  const humint = useExtensionEnabled(HUMINT_EXTENSION_ID).enabled
  const openLeads = useTattooLeadCount(caseId)?.total ?? 0
  const openVoiceLeads = useVoiceLeadCount(caseId)?.total ?? 0
  const [tab, openTab] = useResolutionTab(humint ? RESOLUTION_TABS : FACE_RESOLUTION_TABS)
  return (
    <Tabs value={tab} onValueChange={openTab} className="min-h-0 flex-1">
      <div className="px-4 pb-3 md:px-6">
        <TabsList>
          <TabsTrigger value="duplicates">{t(`${COPY}.tabs.duplicates`)}</TabsTrigger>
          <CountedTab
            value="photoMatching"
            label={t(`${COPY}.tabs.photoMatching`)}
            waiting={{
              count: pendingFaceCount,
              spoken: t(`${COPY}.photoMatching.pendingCount`, { count: pendingFaceCount }),
            }}
          />
          {humint && (
            <CountedTab
              value="tattooMatching"
              label={t(`${COPY}.tabs.tattooMatching`)}
              waiting={{
                count: openLeads,
                spoken: t(`${COPY}.tattooMatching.openCount`, { count: openLeads }),
              }}
            />
          )}
          {humint && (
            <CountedTab
              value="voiceMatching"
              label={t(`${COPY}.tabs.voiceMatching`)}
              waiting={{
                count: openVoiceLeads,
                spoken: t(`${COPY}.voiceMatching.openCount`, { count: openVoiceLeads }),
              }}
            />
          )}
        </TabsList>
      </div>
      <TabsContent value="duplicates" className="flex min-h-0 flex-col">
        {duplicates}
      </TabsContent>
      <TabsContent value="photoMatching" className={PANEL_TAB}>
        <PhotoMatchingPanel caseId={caseId} />
      </TabsContent>
      {humint && (
        <TabsContent value="tattooMatching" className={PANEL_TAB}>
          <Suspense fallback={<Skeleton className="h-40 w-full" />}>
            <TattooLeadsTab caseId={caseId} />
          </Suspense>
        </TabsContent>
      )}
      {humint && (
        <TabsContent value="voiceMatching" className={PANEL_TAB}>
          <Suspense fallback={<Skeleton className="h-40 w-full" />}>
            <VoiceLeadsTab caseId={caseId} />
          </Suspense>
        </TabsContent>
      )}
    </Tabs>
  )
}
